import React, { useEffect, useMemo, useState } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { formatWooPrice, woocommerceApi } from '@/lib/api/services/woocommerce';
import { Loader2, RefreshCw, PlugZap, Unplug, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCatalogInfo } from '@/hooks/useCommerce';
import { motion } from 'framer-motion';
import { WooCommerceOrders } from '@/components/woocommerce/WooCommerceOrders';
import { WooCommerceProducts } from '@/components/woocommerce/WooCommerceProducts';
import { WooCommerceAbandonedCarts } from '@/components/woocommerce/WooCommerceAbandonedCarts';
import { WooCommerceAutoMessages } from '@/components/woocommerce/WooCommerceAutoMessages';
import { AnimatedTabs } from '@/components/reusable/AnimatedTabs';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';

type WooProduct = {
  id: string;
  name: string;
  status?: string;
  price?: string | number;
  stock_status?: string;
  stock_quantity?: number;
  woocommerce_product_id: string;
  sku?: string | null;
  updated_at?: string;
};

const DEFAULT_LIMIT = 25;

const WooCommercePage: React.FC = () => {
  const { selectedAccountId, isLoading: accountsLoading } = useBusinessAccountContext();
  const { data: catalogInfo, isLoading: catalogInfoLoading } = useCatalogInfo();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL State Management
  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      // Create fresh params
      const newParams = new URLSearchParams();
      newParams.set('tab', tab);
      return newParams;
    });
  };

  const [form, setForm] = useState({
    storeUrl: '',
    consumerKey: '',
    consumerSecret: '',
  });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingToCatalog, setSyncingToCatalog] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    storeUrl?: string;
    storeName?: string;
    currency?: string;
    connectedAt?: string | null;
  } | null>(null);
  const [products, setProducts] = useState<WooProduct[]>([]);
  const [error, setError] = useState<string | null>(null);

  const productsPage = parseInt(searchParams.get('page') || '1');
  const setProductsPage = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const [productsPageSize, setProductsPageSize] = useState(20);

  // Check connection — clear previous BA's cached state first so the UI
  // doesn't briefly render the old account's connection info / products.
  useEffect(() => {
    setIsConnected(false);
    setConnectionInfo(null);
    setProducts([]);
    setError(null);
    if (selectedAccountId) {
      checkConnection();
    }
  }, [selectedAccountId]);

  const checkConnection = async () => {
    if (!selectedAccountId) return;
    try {
      const res = await woocommerceApi.checkConnection(selectedAccountId);
      if (res.connected) {
        setIsConnected(true);
        setConnectionInfo({
          storeUrl: res.storeUrl,
          storeName: res.storeName,
          currency: res.currency,
          connectedAt: res.connectedAt,
        });
        loadData();
      } else {
        setIsConnected(false);
        setConnectionInfo(null);
        setProducts([]);
      }
    } catch (err) {
      setIsConnected(false);
    }
  };

  const loadData = async () => {
    if (!selectedAccountId) return;
    setLoadingData(true);
    try {
      await Promise.all([
        loadProducts(),
      ]);
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to load data'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadProducts = async () => {
    if (!selectedAccountId) return;
    const res = await woocommerceApi.getLocalProducts(selectedAccountId, DEFAULT_LIMIT, 0);
    setProducts(res.products || []);
  };

  const handleConnect = async () => {
    if (!selectedAccountId) return;
    if (!form.storeUrl || !form.consumerKey || !form.consumerSecret) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all connection fields.',
        variant: 'destructive',
      });
      return;
    }

    setConnecting(true);
    setError(null);
    try {
      await woocommerceApi.connect({
        businessId: selectedAccountId,
        storeUrl: form.storeUrl,
        consumerKey: form.consumerKey,
        consumerSecret: form.consumerSecret,
      });
      toast({ variant: "success",
        title: 'Connected',
        description: 'Successfully connected to WooCommerce store!',
      });
      setForm({ storeUrl: '', consumerKey: '', consumerSecret: '' });
      await checkConnection();
      setActiveTab('products');
    } catch (err: any) {
      const msg = err?.message || 'Connection failed';
      setError(msg);
      showApiError(err, { fallback: { title: 'Connection Failed', description: msg } });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!selectedAccountId) return;

    setDisconnecting(true);
    try {
      await woocommerceApi.disconnect(selectedAccountId);
      toast({ variant: "success",
        title: 'Disconnected',
        description: 'WooCommerce store disconnected.',
      });
      setDisconnectDialogOpen(false);
      setIsConnected(false);
      setConnectionInfo(null);
      setProducts([]);
      setActiveTab('overview');
    } catch (err: any) {
      const msg = err?.message || 'Disconnect failed';
      showApiError(err, { fallback: { title: 'Disconnect failed', description: msg } });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncProducts = async () => {
    if (!selectedAccountId) return;
    setSyncingProducts(true);
    setError(null);
    try {
      const res = await woocommerceApi.syncProducts(selectedAccountId, 100);
      const count = res.synced || 0;
      toast(count > 0
        ? { title: 'Products Synced', description: `Fetched ${count} ${count === 1 ? 'product' : 'products'} from WooCommerce.` }
        : { title: 'Up to date', description: 'No new products to sync from WooCommerce.' }
      );
      await loadProducts();
    } catch (err: any) {
      const msg = err?.message || 'Failed to sync WooCommerce products';
      setError(msg);
      showApiError(err, { fallback: { title: 'Sync Failed', description: msg } });
    } finally {
      setSyncingProducts(false);
    }
  };

  const handleSyncToCatalog = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync products.',
        variant: 'default',
      });
      return;
    }
    if (!isConnected) {
      toast({
        title: 'WooCommerce Connection Required',
        description: 'Please connect your WooCommerce store first.',
        variant: 'default',
      });
      return;
    }
    const catalogStatusKnown = typeof catalogInfo !== 'undefined';
    if (catalogStatusKnown && (!catalogInfo || catalogInfo.status !== 'connected')) {
      toast({
        title: 'WhatsApp Catalog Not Connected',
        description: 'Please connect a WhatsApp catalog first. Go to the Commerce page to set up your catalog.',
        variant: 'destructive',
      });
      return;
    }

    setSyncingToCatalog(true);
    setError(null);
    try {
      const res = await woocommerceApi.syncToCatalog(selectedAccountId, selectedAccountId, {
        limit: 100,
        skipExisting: true,
        catalogId: catalogInfo?.whatsapp_catalog_id || catalogInfo?.id,
      });
      toast({ variant: "success",
        title: 'Catalog Sync Started',
        description: res?.message || 'Sync started in the background.',
      });
    } catch (e: any) {
      let errorMsg = 'Failed to sync products to WhatsApp catalog';
      if (e.message) errorMsg = e.message;
      if (errorMsg.includes('catalog not connected')) {
        errorMsg = 'WhatsApp catalog is not connected. Please go to the Commerce page to set up your catalog.';
      }

      setError(errorMsg);
      showApiError(e, { fallback: { title: 'Sync Failed', description: errorMsg } });
    } finally {
      setSyncingToCatalog(false);
    }
  };

  const productsColumns: Column<WooProduct>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Product Name',
      render: (_, prod) => (
        <div className="flex flex-col">
          <span className="font-medium text-xs sm:text-sm">{prod.name}</span>
          {prod.sku && <span className="text-xs text-muted-foreground">SKU: {prod.sku}</span>}
        </div>
      ),
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, prod) => (
        <Badge variant={prod.status === 'publish' ? 'default' : 'secondary'} className="text-xs">
          {prod.status || 'unknown'}
        </Badge>
      ),
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'price',
      header: 'Price',
      render: (_, prod) => (
        <span className="text-xs sm:text-sm">
          {formatWooPrice(Number(prod.price || 0), connectionInfo?.currency || 'USD')}
        </span>
      ),
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (_, prod) => (
        <div className="flex items-center gap-2">
          <span className={`text-xs sm:text-sm ${prod.stock_status === 'instock' ? 'text-green-600' : 'text-red-600'}`}>
            {prod.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
          </span>
          {prod.stock_quantity !== undefined && prod.stock_quantity !== null && (
            <span className="text-xs text-muted-foreground">({prod.stock_quantity})</span>
          )}
        </div>
      ),
      className: 'text-xs sm:text-sm',
    },
  ], [connectionInfo?.currency]);

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home/integrations')}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <div>
            <CardTitle className="text-xl sm:text-2xl">WooCommerce Integration</CardTitle>
            <CardDescription className="text-sm">
              Connect your WooCommerce store to sync products and orders
            </CardDescription>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <AnimatedTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          layoutId="woo-tab-pill"
          tabs={[
            { label: 'Overview', value: 'overview' },
            { label: 'Products', value: 'products', disabled: !isConnected },
            { label: 'Orders', value: 'orders', disabled: !isConnected },
            { label: 'Abandoned Cart', value: 'abandoned', disabled: !isConnected },
            { label: 'Auto Messages', value: 'auto-messages', disabled: !isConnected },
          ]}
        />

        <TabsContent value="overview">
          {isConnected ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlugZap className="h-5 w-5 text-green-600" />
                  Connected to {connectionInfo?.storeName || connectionInfo?.storeUrl}
                </CardTitle>
                <CardDescription>
                  Your store is currently connected and syncing data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 text-sm">
                  <div>
                    <span className="font-medium">Store URL:</span> {connectionInfo?.storeUrl}
                  </div>
                  <div>
                    <span className="font-medium">Currency:</span> {connectionInfo?.currency || 'USD'}
                  </div>
                  <div>
                    <span className="font-medium">Connected At:</span>{' '}
                    {connectionInfo?.connectedAt
                      ? new Date(connectionInfo.connectedAt).toLocaleString()
                      : '—'}
                  </div>
                </div>
                <div className="pt-4">
                  <Button
                    variant="destructive"
                    onClick={() => setDisconnectDialogOpen(true)}
                    disabled={disconnecting}
                    className="flex items-center gap-2"
                  >
                    {disconnecting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Disconnecting...
                      </>
                    ) : (
                      <>
                        <Unplug className="h-4 w-4" />
                        Disconnect
                      </>
                    )}
                  </Button>
                </div>

                <DisconnectConfirmDialog
                  open={disconnectDialogOpen}
                  onOpenChange={setDisconnectDialogOpen}
                  onConfirm={handleDisconnect}
                  isLoading={disconnecting}
                  integration="woocommerce"
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlugZap className="h-5 w-5" />
                  Connect Store
                </CardTitle>
                <CardDescription>
                  Provide your WooCommerce store credentials. We use WooCommerce REST API keys over HTTPS.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="storeUrl">Store URL</Label>
                    <Input
                      id="storeUrl"
                      placeholder="https://yourstore.com"
                      value={form.storeUrl}
                      onChange={(event) => setForm((prev) => ({ ...prev, storeUrl: event.target.value }))}
                      disabled={connecting}
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="consumerKey">Consumer Key</Label>
                      <Input
                        id="consumerKey"
                        placeholder="ck_xxxxx"
                        value={form.consumerKey}
                        onChange={(event) => setForm((prev) => ({ ...prev, consumerKey: event.target.value }))}
                        disabled={connecting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="consumerSecret">Consumer Secret</Label>
                      <Input
                        id="consumerSecret"
                        placeholder="cs_xxxxx"
                        value={form.consumerSecret}
                        onChange={(event) => setForm((prev) => ({ ...prev, consumerSecret: event.target.value }))}
                        disabled={connecting}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleConnect}
                    disabled={connecting || !selectedAccountId}
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      'Save & Sync'
                    )}
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs mt-2">
                  Generate API credentials from WooCommerce → Settings → Advanced → REST API. Ensure read permissions for products and orders.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
              <CardDescription>
                Preview the latest WooCommerce products synced for this business account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <WooCommerceProducts
                selectedAccountId={selectedAccountId}
                isConnected={isConnected}
                currency={connectionInfo?.currency}
                onAddProduct={() => { }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          {isConnected && (
            <WooCommerceOrders
              selectedAccountId={selectedAccountId}
              isConnected={isConnected}
              currency={connectionInfo?.currency}
            />
          )}
          {!isConnected && (
            <div className="text-center py-6 sm:py-8 space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Please connect your WooCommerce store first to view orders.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="abandoned" className="mt-4">
          {isConnected && (
            <WooCommerceAbandonedCarts
              selectedAccountId={selectedAccountId}
              isConnected={isConnected}
              currency={connectionInfo?.currency}
            />
          )}
          {!isConnected && (
            <div className="text-center py-6 sm:py-8 space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">Please connect your WooCommerce store first to view abandoned carts.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="auto-messages" className="mt-4">
          <WooCommerceAutoMessages
            businessAccountId={selectedAccountId || ''}
            isConnected={isConnected}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WooCommercePage;
